import { ref } from "vue";
import { defineStore } from "pinia";
import { PAGE_TYPE } from "@supplier/meta/src/common";

import type { LocationQuery, RouteLocationNormalizedLoaded } from "vue-router";

export interface HistoryRouteProps {
    /** 路由历史标题 */
    title: string;
    /** router路径 */
    path: string;
    /** router名称 */
    name: string;
    /** 面包屑路径  例：进销存管理/采购管理/订单管理 */
    breadcrumb: string;
}

/**
 * 菜单最近访问功能 （路由历史）
 */
const HISTORY_ROUTE_STORE_PREFIX = "zhongsheng-web-history-route";
const HISTORY_ROUTE_STORE_ID = "zhongsheng-web-history-route-store";
const resolveStoreName = () => {
    try {
        const rawUserStore = localStorage.getItem("zhongsheng-web-user");
        const userStore = rawUserStore
            ? (JSON.parse(rawUserStore) as {
                  userInfo?: { customer?: { id?: string | number }; mainBody?: { id?: string | number } };
              })
            : null;
        const customerId = userStore?.userInfo?.customer?.id;
        const mainBodyId = userStore?.userInfo?.mainBody?.id;
        return customerId && mainBodyId
            ? `${HISTORY_ROUTE_STORE_PREFIX}-${mainBodyId}-${customerId}`
            : HISTORY_ROUTE_STORE_PREFIX;
    } catch {
        return HISTORY_ROUTE_STORE_PREFIX;
    }
};
// 仅保留最新的10个 路由历史
const DEFAULT_LIST_NUM = 10;

const getStorageHistoryList = (storageKey: string) => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return [] as HistoryRouteProps[];
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed) ? (parsed as HistoryRouteProps[]) : [];
    } catch {
        return [] as HistoryRouteProps[];
    }
};

const setStorageHistoryList = (storageKey: string, list: HistoryRouteProps[]) => {
    localStorage.setItem(storageKey, JSON.stringify(list));
};

export const useHistoryRouteStore = defineStore(HISTORY_ROUTE_STORE_ID, () => {
    const storageKey = ref(resolveStoreName());
    const historyRouteList = ref<HistoryRouteProps[]>(getStorageHistoryList(storageKey.value));
    const isOpen = ref(false);

    const syncStorageContext = () => {
        const nextStorageKey = resolveStoreName();
        if (nextStorageKey === storageKey.value) return;
        storageKey.value = nextStorageKey;
        historyRouteList.value = getStorageHistoryList(nextStorageKey);
    };

    const persistHistoryRouteList = () => {
        setStorageHistoryList(storageKey.value, historyRouteList.value);
    };

    const openPopover = () => {
        syncStorageContext();
        isOpen.value = true;
    };

    const closePopover = () => {
        isOpen.value = false;
    };

    /**
     * 添加最近访问记录
     * - 相同 path 会被移动到列表顶部
     * - 仅保留最新的 DEFAULT_LIST_NUM 条
     */
    const addHistoryRoute = (route: RouteLocationNormalizedLoaded) => {
        syncStorageContext();
        if (!route || (route?.meta?.activeMenu && route?.meta?.pageType !== PAGE_TYPE.LIST.code)) return;

        const resolveTitle = (record: { meta?: Record<string, unknown> }, query: LocationQuery) => {
            const title = record?.meta?.title;
            return typeof title === "function" ? title(query) : (title as string | undefined);
        };

        const breadcrumbList = route.matched
            .map(record => resolveTitle(record, route.query))
            .filter((item): item is string => Boolean(item));

        const menu: HistoryRouteProps = {
            title:
                resolveTitle(route, route.query) ||
                breadcrumbList[breadcrumbList.length - 1] ||
                (route.name as string) ||
                route.path,
            path: route.fullPath || route.path,
            name: (route.name as string) || route.path,
            breadcrumb: breadcrumbList.join("/")
        };

        if (!menu.path) return;
        // 生成新队列：去重后把最新项放首位，再截断长度，单次赋值确保视图及时更新
        historyRouteList.value = [menu, ...historyRouteList.value.filter(item => item.path !== menu.path)].slice(
            0,
            DEFAULT_LIST_NUM
        );
        persistHistoryRouteList();
    };

    return {
        isOpen,
        historyRouteList,
        openPopover,
        closePopover,
        addHistoryRoute
    };
});
